/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useGenerateAsyncIndustryUseCase.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { NetworkStatus } from '@apollo/client';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { useAsyncBulkGenerateIndustryUseCaseMutation, useAsyncBulkGetIndustryUseCaseLazyQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/IndustryUseCaseRpc.hooks';
import { useUseCaseSession } from '../utils/industryUseCaseUtils.migrated';
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL = 3000;
export const useGenerateAsyncIndustryUseCase = ({
  primaryContentId,
  contentType,
  contentPortalId,
  videoId,
  industry
}) => {
  var _pollData$tasks, _useCaseResponse$indu, _useCaseResponse$indu2;
  const [pollCount, setPollCount] = useState(0);
  const [hasStartedPolling, setHasStartedPolling] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [useCaseLoadingState, setUseCaseLoadingState] = useState(false);
  const [activeIndustry, setActiveIndustry] = useState(industry);

  // The use case is generated per video, so key the store by videoId (falling
  // back to primaryContentId) to avoid collisions between videos in a lesson.
  const contentId = videoId !== null && videoId !== void 0 ? videoId : primaryContentId;

  // Ref keeps the industry stable for the async mutation/poll callbacks.
  const activeIndustryRef = useRef(industry);
  const prevPortalIndustryRef = useRef(industry);
  const prevContentIdRef = useRef(contentId);
  const {
    getUseCaseForIndustry,
    setUseCaseTaskId,
    setUseCaseData
  } = useUseCaseSession();
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const setSelectedIndustry = useCallback(selectedIndustry => {
    activeIndustryRef.current = selectedIndustry;
    setActiveIndustry(selectedIndustry);
  }, []);
  const cachedUseCaseForActiveIndustry = getUseCaseForIndustry(contentId, contentType, activeIndustry);

  // Counts as tracked whether we have a completed useCase OR an in-flight taskId,
  // so we don't re-trigger generation while a task is already running.
  const isCachedForActiveIndustry = !!(cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.taskId || cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.useCase);
  const [generateTaskId, {
    data: generateData,
    loading: generateLoading,
    error: generateError,
    reset: resetMutation
  }] = useAsyncBulkGenerateIndustryUseCaseMutation({
    onCompleted: response => {
      var _response$result;
      const task = response === null || response === void 0 || (_response$result = response.result) === null || _response$result === void 0 || (_response$result = _response$result.tasks) === null || _response$result === void 0 ? void 0 : _response$result[0];
      if (task !== null && task !== void 0 && task.taskId) {
        setUseCaseTaskId({
          contentType,
          contentId,
          taskId: task.taskId,
          industry: activeIndustryRef.current
        });
        setPollCount(0);
      }
    }
  });
  const [pollForUseCase, {
    data: pollData,
    loading: pollLoading,
    error: pollError,
    networkStatus: pollNetworkStatus,
    stopPolling
  }] = useAsyncBulkGetIndustryUseCaseLazyQuery({
    pollInterval: POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  });
  const resetPollingState = useCallback(() => {
    stopPolling();
    setHasStartedPolling(false);
    setPollCount(0);
    setUseCaseLoadingState(false);
    setTimeoutError(false);
  }, [stopPolling]);

  // Reset state when navigating between videos
  useEffect(() => {
    if (prevContentIdRef.current !== contentId) {
      resetPollingState();
      resetMutation();
      if (!industry) setSelectedIndustry(undefined);
      prevContentIdRef.current = contentId;
    }
  }, [contentId, industry, resetPollingState, resetMutation, setSelectedIndustry]);

  // Re-sync when portal settings industry changes (also bootstraps on first async load)
  useEffect(() => {
    if (!industry || industry === prevPortalIndustryRef.current) return;
    prevPortalIndustryRef.current = industry;
    resetPollingState();
    resetMutation();
    setSelectedIndustry(industry);
  }, [industry, resetPollingState, resetMutation, setSelectedIndustry]);

  // Only use poll data if it matches the current video (prevents stale Apollo cache data)
  const useCaseResponse = pollData === null || pollData === void 0 || (_pollData$tasks = pollData.tasks) === null || _pollData$tasks === void 0 ? void 0 : _pollData$tasks[0];
  const shouldUsePollData = hasStartedPolling && !(cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.useCase) && prevContentIdRef.current === contentId;
  const useCase = shouldUsePollData ? useCaseResponse === null || useCaseResponse === void 0 || (_useCaseResponse$indu = useCaseResponse.industryUseCaseResponse) === null || _useCaseResponse$indu === void 0 ? void 0 : _useCaseResponse$indu.useCase : undefined;
  const correlationId = shouldUsePollData ? useCaseResponse === null || useCaseResponse === void 0 || (_useCaseResponse$indu2 = useCaseResponse.industryUseCaseResponse) === null || _useCaseResponse$indu2 === void 0 ? void 0 : _useCaseResponse$indu2.correlationId : undefined;
  const status = shouldUsePollData ? useCaseResponse === null || useCaseResponse === void 0 ? void 0 : useCaseResponse.status : undefined;
  const useCaseReady = status === 'COMPLETED';
  const retryGeneration = !!pollError || status === 'NOT_FOUND' || status === 'FAILED';
  const isPolling = pollNetworkStatus === NetworkStatus.poll || pollLoading;

  // Store the use case in Zustand for persistence across video navigation
  useEffect(() => {
    if (useCase && !(cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.useCase)) {
      setUseCaseData({
        contentType,
        contentId,
        useCase,
        correlationId,
        industry: activeIndustryRef.current
      });
    }
  }, [useCase, cachedUseCaseForActiveIndustry === null || cachedUseCaseForActiveIndustry === void 0 ? void 0 : cachedUseCaseForActiveIndustry.useCase, setUseCaseData, contentType, contentId, correlationId]);

  // Track polling progress and stop when complete/failed/timeout
  useEffect(() => {
    if (!useCaseReady && isPolling) {
      setPollCount(prev => prev + 1);
      setUseCaseLoadingState(true);
    }
    if (useCaseReady || retryGeneration || pollCount > MAX_POLL_ATTEMPTS) {
      stopPolling();
      setPollCount(0);
      setUseCaseLoadingState(false);
      setTimeoutError(pollCount > MAX_POLL_ATTEMPTS);
    }
    // Intentionally excluding pollCount from deps to avoid re-triggering effect on every poll increment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling, pollData, pollError, isPolling, useCaseReady, retryGeneration]);

  // Build variables for use case generation RPC. The explicit type keeps the
  // industry enum from widening to `symbol` inside the videos array literal.
  const useCaseVariables = useMemo(() => {
    if (!contentPortalId || !videoId || !activeIndustry) {
      return undefined;
    }
    if (typeof activeIndustry === 'symbol') return undefined;
    return {
      videos: [{
        videoPortalId: contentPortalId,
        videoId,
        industry: activeIndustry
      }]
    };
  }, [contentPortalId, videoId, activeIndustry]);
  const shouldGenerate = useMemo(() => aiEnabled && !isAcceptanceTest() && !!useCaseVariables && !isCachedForActiveIndustry && !generateLoading && !generateData && !generateError, [aiEnabled, useCaseVariables, isCachedForActiveIndustry, generateLoading, generateData, generateError]);

  // Trigger generation only once an industry is active and nothing is cached
  useEffect(() => {
    if (shouldGenerate && useCaseVariables) {
      generateTaskId({
        variables: useCaseVariables
      }).catch(() => {});
    }
  }, [shouldGenerate, useCaseVariables, generateTaskId]);

  // Start polling once we have a taskId from generation
  useEffect(() => {
    if (isCachedForActiveIndustry && cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.taskId && !(cachedUseCaseForActiveIndustry !== null && cachedUseCaseForActiveIndustry !== void 0 && cachedUseCaseForActiveIndustry.useCase) && !hasStartedPolling) {
      pollForUseCase({
        variables: {
          taskIds: [cachedUseCaseForActiveIndustry.taskId]
        }
      });
      setHasStartedPolling(true);
    }
  }, [isCachedForActiveIndustry, cachedUseCaseForActiveIndustry === null || cachedUseCaseForActiveIndustry === void 0 ? void 0 : cachedUseCaseForActiveIndustry.taskId, cachedUseCaseForActiveIndustry === null || cachedUseCaseForActiveIndustry === void 0 ? void 0 : cachedUseCaseForActiveIndustry.useCase, hasStartedPolling, pollForUseCase]);
  const generateForIndustry = useCallback(selectedIndustry => {
    const cachedEntry = getUseCaseForIndustry(contentId, contentType, selectedIndustry);
    setSelectedIndustry(selectedIndustry);
    if (cachedEntry !== null && cachedEntry !== void 0 && cachedEntry.useCase || cachedEntry !== null && cachedEntry !== void 0 && cachedEntry.taskId) return;
    resetMutation();
    resetPollingState();
  }, [contentId, contentType, getUseCaseForIndustry, resetMutation, resetPollingState, setSelectedIndustry]);
  const refetchUseCase = useCallback(() => {
    resetMutation();
    setUseCaseTaskId({
      contentType,
      contentId,
      taskId: undefined,
      industry: activeIndustryRef.current
    });
    resetPollingState();
  }, [resetMutation, setUseCaseTaskId, resetPollingState, contentType, contentId]);
  const apolloError = generateError || pollError;
  const useCaseError = !!apolloError || timeoutError || retryGeneration;
  return {
    activeIndustry,
    useCase: (cachedUseCaseForActiveIndustry === null || cachedUseCaseForActiveIndustry === void 0 ? void 0 : cachedUseCaseForActiveIndustry.useCase) || useCase,
    correlationId: (cachedUseCaseForActiveIndustry === null || cachedUseCaseForActiveIndustry === void 0 ? void 0 : cachedUseCaseForActiveIndustry.correlationId) || correlationId,
    useCaseError,
    useCaseLoading: generateLoading || useCaseLoadingState,
    refetchUseCase,
    generateForIndustry
  };
};